import { useState } from "react";
import {
  Stack,
  Typography,
  TextField,
  InputAdornment,
  Switch,
  Collapse,
  CardHeader,
  Box,
  Button
} from "@mui/material";
import {
  GppBadRounded as DisabledIcon,
  GppGoodRounded as EnabledIcon,
  InfoRounded as InfoIcon
} from "@mui/icons-material";

import { useStore, observer } from "../../../../service/mobx";
import { fetchFromApiServer } from "../../../../service/graph";

function Credits({ buyCredits = false, editAutoReload = false }) {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState();
  const [creditsToBuy, setCreditsToBuy] = useState(buyCredits ? 25.0 : 0);
  const { dialog, user } = useStore();
  const billing = user.profile.billing ?? {
    period: { credits: { balance: 0 } }
  };
  const {
    to = 0.0,
    when = 0.0,
    limit = 0.0,
    enabled = false
  } = billing?.reload ?? {};

  const [autoReloadUpdate, setAutoReloadUpdate] = useState({
    enabled,
    expanded: editAutoReload || enabled,
    threshold: when || 5,
    topUpAmount: to || 15,
    maxSpend: limit,
    noError: { threshold: true, topUpAmount: true, maxSpend: true }
  });
  const autoReloadBeingDisabled =
    autoReloadUpdate.expanded === false && enabled;
  const changedAutoReload =
    autoReloadUpdate.noError.threshold &&
    autoReloadUpdate.noError.topUpAmount &&
    autoReloadUpdate.noError.maxSpend &&
    !!autoReloadUpdate.threshold &&
    !!autoReloadUpdate.topUpAmount &&
    ((autoReloadUpdate.expanded &&
      (autoReloadUpdate.threshold !== when ||
        autoReloadUpdate.topUpAmount !== to ||
        autoReloadUpdate.maxSpend !== limit)) ||
      autoReloadBeingDisabled);

  const balance = billing.period?.credits?.balance ?? 0;
  const manualPurchase = creditsToBuy || 0;

  // --- New creditPurchase logic ---
  let creditPurchase = manualPurchase;

  if (changedAutoReload) {
    const tentativeBalance = balance + manualPurchase;

    if (tentativeBalance < autoReloadUpdate.threshold) {
      // Auto-reload would fire immediately and bring balance up to topUpAmount
      creditPurchase = Math.max(0, autoReloadUpdate.topUpAmount - balance);
    }
    // else: creditPurchase stays as manualPurchase
  }

  const subtotal = creditPurchase;
  const taxes = 0.0;
  const total = subtotal + taxes;

  // noPurchase = user didn't type any "Buy credits" amount
  const noPurchaseInput = creditsToBuy === 0 || creditsToBuy === "";
  const disabled = pending || (noPurchaseInput && changedAutoReload === false);

  const onlyAutoReloadPurchase = creditPurchase > 0 && noPurchaseInput;

  return (
    <Stack spacing={3}>
      <Stack spacing={3}>
        {/* --- CREDITS INPUT --- */}
        <Input
          fullWidth
          value={creditsToBuy}
          label="Buy credits"
          placeholder="25"
          onChange={setCreditsToBuy}
        />

        {/* --- AUTO RELOAD SECTION --- */}
        <AutoReload
          editAutoReload={editAutoReload}
          autoReloadUpdate={autoReloadUpdate}
          setAutoReloadUpdate={setAutoReloadUpdate}
          creditPurchase={creditPurchase}
          balance={balance}
          willTriggerImmediateReload={
            changedAutoReload &&
            autoReloadUpdate.expanded &&
            balance + manualPurchase < autoReloadUpdate.threshold
          }
        />
      </Stack>

      {/* --- ACTIONS --- */}
      <Stack spacing={2}>
        {/* --- SUMMARY --- */}
        <Collapse mountOnEnter unmountOnExit in={!!error}>
          <CardHeader
            title={error?.title}
            subheader={error?.subheader}
            avatar={<InfoIcon sx={{ color: "var(--colors-error-20)" }} />}
            sx={{
              border: 1,
              alignItems: "flex-start",
              borderRadius: "var(--shape-md-round)",
              borderColor: "var(--colors-error-80)",
              bgcolor: "var(--error-container)",
              "& .MuiCardHeader-avatar": {
                mt: 0.5
              }
            }}
            slotProps={{
              title: {
                variant: "titleMd",
                color: "var(--colors-error-30)"
              },
              subheader: {
                variant: "bodyMd",
                color: "var(--colors-error-20)",
                sx: { textWrap: "balance" }
              }
            }}
          />
        </Collapse>
        <div>
          <Collapse mountOnEnter unmountOnExit in={creditPurchase > 0}>
            <Stack
              p={2}
              spacing={2}
              borderTop={1}
              borderBottom={1}
              bgcolor="var(--surface-dim)"
              borderColor="var(--outline-variant)"
            >
              <Typography align="center" variant="titleLg" fontWeight={500}>
                Buying {creditPurchase} credit{creditPurchase === 1 ? "" : "s"}
              </Typography>
              <div>
                {[
                  ["Subtotal", subtotal],
                  ["Estimated taxes", taxes],
                  ["Total", total]
                ].map(([label, amount]) => (
                  <Stack
                    key={label}
                    px={2}
                    direction="row"
                    justifyContent="space-between"
                  >
                    <Typography variant="titleMd">{label}</Typography>
                    <Typography variant="titleMd">
                      ${amount.toFixed?.(2)}
                    </Typography>
                  </Stack>
                ))}
              </div>
              <Typography
                align="center"
                variant="labelSm"
                color="var(--secondary-color)"
                sx={{ textWrap: "balance" }}
              >
                Credits are available immediately
              </Typography>
            </Stack>
          </Collapse>
        </div>
        <Button
          fullWidth
          variant="contained"
          loading={pending}
          loadingPosition="start"
          disabled={disabled}
          sx={{
            height: 56,
            fontSize: "18px !important",
            borderRadius: "var(--shape-sm)"
          }}
          onClick={async () => {
            try {
              setError();
              setPending(true);

              const needsPurchase = creditPurchase > 0;

              if (needsPurchase) {
                const { error } = await fetchFromApiServer({
                  path: "billing/credits/purchase",
                  body: { amountUSD: creditPurchase }
                });

                if (error) {
                  setError({
                    title: "Card declined",
                    subheader:
                      "Your bank rejected the transaction. Check the card and try again."
                  });

                  throw error;
                }
              }

              if (changedAutoReload) {
                const { error } = await fetchFromApiServer({
                  path: "billing/credits/options",
                  body: {
                    reload: {
                      enabled: autoReloadUpdate.expanded,
                      when: autoReloadUpdate.threshold,
                      to: autoReloadUpdate.topUpAmount,
                      limit: autoReloadUpdate.maxSpend
                    },
                    scaleUp: { enabled: false }
                  }
                });

                if (error) {
                  setError({
                    title: needsPurchase
                      ? "Purchase successful but cannot update auto-reload"
                      : "Cannot update auto-reload",
                    subheader: (
                      <>
                        Please report this in{" "}
                        <Box
                          component="a"
                          target="_blank"
                          href="https://discord.gg/ZydhEJTd3R"
                          sx={{ color: "var(--error-color)" }}
                        >
                          Discord
                        </Box>{" "}
                        or at{" "}
                        <Box
                          component="a"
                          target="_blank"
                          href="mailto:support@bytez.com"
                          sx={{ color: "var(--error-color)" }}
                        >
                          support@bytez.com
                        </Box>
                      </>
                    )
                  });

                  throw error;
                }
              }

              dialog.set.open(false);
            } catch (error) {
              console.error(error);
            } finally {
              setPending(false);
            }
          }}
        >
          {creditPurchase === 0
            ? autoReloadBeingDisabled
              ? "Pause auto-reload"
              : enabled
              ? "Update"
              : "Enable auto-reload"
            : onlyAutoReloadPurchase
            ? "Update & Purchase"
            : pending
            ? "Processing ..."
            : `Buy $${total.toFixed(0)} of credits`}
        </Button>
      </Stack>
    </Stack>
  );
}

export default observer(Credits);

const AutoReload = ({
  autoReloadUpdate,
  setAutoReloadUpdate,
  balance,
  creditPurchase,
  willTriggerImmediateReload
}) => (
  <Stack
    spacing={2}
    p={2}
    bgcolor="var(--surface-dim)"
    borderRadius="var(--shape-md-round)"
  >
    <CardHeader
      title={`Auto-reload ${autoReloadUpdate.enabled ? "enabled" : "disabled"}`}
      subheader={
        autoReloadUpdate.enabled
          ? undefined
          : "Enable to avoid service disruptions when credits are low"
      }
      avatar={
        autoReloadUpdate.enabled ? (
          <EnabledIcon color="success" />
        ) : (
          <DisabledIcon sx={{ color: "var(--tertiary-color)" }} />
        )
      }
      action={
        <Switch
          color="default"
          checked={autoReloadUpdate.expanded}
          onChange={() =>
            setAutoReloadUpdate(autoReloadUpdate => ({
              ...autoReloadUpdate,
              expanded: !autoReloadUpdate.expanded
            }))
          }
        />
      }
      sx={{
        p: 0,
        alignItems: autoReloadUpdate.enabled ? undefined : "flex-start",
        "& .MuiCardHeader-avatar": {
          mt: autoReloadUpdate.enabled ? undefined : 0.5
        }
      }}
      slotProps={{
        title: {
          variant: "titleMd",
          color: autoReloadUpdate.enabled
            ? "success.main"
            : "var(--tertiary-color)"
        },
        subheader: {
          variant: "bodyMd",
          sx: { textWrap: "balance" }
        }
      }}
    />

    {/* Conditional Inputs */}
    <Collapse mountOnEnter unmountOnExit in={autoReloadUpdate.expanded}>
      <Stack spacing={1}>
        <Stack spacing={1} direction={{ compact: "column", expanded: "row" }}>
          <Input
            value={autoReloadUpdate.threshold}
            placeholder="5"
            min={5}
            label="When your credits reach"
            onChange={threshold =>
              setAutoReloadUpdate(autoReloadUpdate => ({
                ...autoReloadUpdate,
                threshold,
                noError: {
                  threshold: !(threshold < 5),
                  topUpAmount: !(autoReloadUpdate.topUpAmount < threshold + 10),
                  maxSpend: !(
                    autoReloadUpdate.maxSpend !== 0 &&
                    autoReloadUpdate.maxSpend < autoReloadUpdate.topUpAmount
                  )
                }
              }))
            }
            helperText={
              autoReloadUpdate.noError.threshold
                ? undefined
                : "Must be at least $5"
            }
          />
          <Input
            value={autoReloadUpdate.topUpAmount}
            placeholder="15"
            min={Math.max(autoReloadUpdate.threshold, 5) + 10}
            label="Bring your credits to"
            onChange={topUpAmount =>
              setAutoReloadUpdate(autoReloadUpdate => ({
                ...autoReloadUpdate,
                topUpAmount,
                noError: {
                  threshold: !(autoReloadUpdate.threshold < 5),
                  topUpAmount: !(topUpAmount < autoReloadUpdate.threshold + 10),
                  maxSpend: !(
                    autoReloadUpdate.maxSpend !== 0 &&
                    autoReloadUpdate.maxSpend < topUpAmount
                  )
                }
              }))
            }
            helperText={
              autoReloadUpdate.noError.topUpAmount
                ? undefined
                : `Must be at least $${
                    Math.max(autoReloadUpdate.threshold, 5) + 10
                  }`
            }
          />
        </Stack>
        <Input
          value={autoReloadUpdate.maxSpend}
          placeholder="100"
          min={Math.max(autoReloadUpdate.threshold, 5) + 10}
          label="Limit monthly spend to"
          onChange={maxSpend =>
            setAutoReloadUpdate(autoReloadUpdate => ({
              ...autoReloadUpdate,
              maxSpend,
              noError: {
                threshold: !(autoReloadUpdate.threshold < 5),
                topUpAmount: !(
                  autoReloadUpdate.topUpAmount <
                  autoReloadUpdate.threshold + 10
                ),
                maxSpend: !(
                  maxSpend !== 0 && maxSpend < autoReloadUpdate.topUpAmount
                )
              }
            }))
          }
          helperText={
            autoReloadUpdate.noError.maxSpend
              ? undefined
              : `Must be at least $${
                  Math.max(autoReloadUpdate.threshold, 5) + 10
                } to allow for one reload`
          }
        />
        <Collapse mountOnEnter unmountOnExit in={willTriggerImmediateReload}>
          <CardHeader
            avatar={<InfoIcon sx={{ color: "var(--secondary-color)" }} />}
            title="Your balance is low"
            subheader={
              <>
                Your <strong>${balance}</strong> balance is below{" "}
                <strong>${autoReloadUpdate.threshold}</strong>, so we'll add{" "}
                <strong>${creditPurchase}</strong> now to reach{" "}
                <strong>${autoReloadUpdate.topUpAmount}</strong>
              </>
            }
            slotProps={{
              title: {
                variant: "titleSm",
                color: "var(--secondary-color)"
              },
              subheader: {
                variant: "bodySm",
                color: "var(--secondary-on-container)",
                sx: { textWrap: "balance" }
              }
            }}
            sx={{
              p: 1,
              pl: 2,
              borderRadius: "var(--shape-sm)",
              bgcolor: "var(--secondary-container)"
            }}
          />
        </Collapse>
      </Stack>
    </Collapse>
  </Stack>
);

const Input = ({ value, label, placeholder, min, onChange, helperText }) => (
  <TextField
    fullWidth
    type="number"
    variant="filled"
    value={value || ""}
    label={label}
    placeholder={placeholder}
    helperText={helperText}
    error={helperText !== undefined}
    onChange={event =>
      onChange(Math.max(parseFloat(event.target.value) || 0.0, 0.0))
    }
    sx={{
      "& .MuiInputLabel-root": {
        typography: "titleLg",
        fontWeight: 500,
        color: "var(--surface-on-color)",
        "&.Mui-error": {
          color: "var(--surface-on-color)"
        }
      }
    }}
    slotProps={{
      formHelperText: {
        sx: { typography: "labelMd", m: 0, pl: 0.5, pt: 1 }
      },
      htmlInput: { min },
      input: {
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <Typography
              typography="bodyLg"
              fontWeight={200}
              color="var(--secondary-color)"
            >
              $
            </Typography>
          </InputAdornment>
        ),
        sx: {
          typography: "titleLg",
          borderRadius: "var(--shape-sm)",
          bgcolor: "var(--surface-container-lowest)",
          transition: "all 1s var(--motion-easing-emphasized)",

          "&:hover": {
            bgcolor: "var(--surface-container-low)"
          },
          "&.Mui-focused": {
            bgcolor: "var(--surface-container-color)"
          }
        }
      }
    }}
  />
);
